import CommonInputField from "../common/CommonInputField";
import CommonDatePicker from "../common/CommonDatePicker";
import CustomSelect from "../common/CustomSelect";
import RoundedIconContainer from "../common/RoundedIconContainer";
import BinSvg from "../../../../common/svgs/BinSvg";
import CheckSingleIcon from "../../svg/CheckSingleIcon";
import React, { useState } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { format } from "date-fns";

type Props = {
  unit: string;
  setEditMode: (value: boolean) => void;
  value: string;
  setValue: (value: string) => void;
  date: string | undefined;
  setDate: (value: string) => void;
  // Qualitative biomarkers pick their string value from `qoptions` instead of
  // typing a number.
  qualitative?: boolean;
  qoptions?: string[];
  // When provided, the row confirms inline via a check icon next to the bin
  // (Figma 16330:70435) instead of an external Save button.
  onSave?: () => void;
  saveDisabled?: boolean;
};

const AddNewBiomarkerWidget = ({
  unit,
  setEditMode,
  value,
  setValue,
  date,
  setDate,
  qualitative,
  qoptions,
  onSave,
  saveDisabled,
}: Props) => {
  const currentDate = new Date();
  const theme = useTheme();

  const [error, setError] = useState<string>("");

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = event.target.value;
    if (newValue.length > 8) {
      setError("Maximum limit reached: Only 8 characters allowed.");
      return;
    }
    if (newValue.startsWith(".")) {
      newValue = "0" + newValue;
    }
    if (
      newValue.length > 2 &&
      newValue.startsWith("0") &&
      !newValue.startsWith("0.")
    ) {
      newValue = newValue.replace(/^0+/, ""); // Remove leading zeros except for "0."
    }
    setError("");
    setValue(newValue);
  };

  const handleChangeDate = (newValue: Date) => {
    if (newValue === null) {
      return;
    }
    const formattedValue = format(new Date(newValue), "yyyy-MM-dd");
    setDate(formattedValue);
  };

  return (
    <div style={styles.modalCard(theme)}>
      <div style={{ flex: 1, marginBottom: qualitative ? "0px" : "-4px" }}>
        {qualitative ? (
          <CustomSelect
            label={"Value"}
            options={qoptions ?? []}
            value={value || null}
            onChange={(selected) => setValue(selected ?? "")}
            borderless
            // Keeps the chevron at the end of the value column, right after
            // the text (Figma 16330:70435) instead of drifting toward Date.
            // Longer answers ("Deletion or Duplication Detected") truncate
            // with an ellipsis inside this width.
            customStyles={{ width: "210px" }}
            placeholder={"Select option"}
          />
        ) : (
          <CommonInputField
            label={"Value"}
            type={"numeric"}
            unit={unit}
            handleChange={handleChange}
            value={value}
            error={error}
          />
        )}
      </div>
      <div style={{ flex: 1, marginBottom: "-8px" }}>
        <CommonDatePicker
          label={"Date"}
          value={date}
          handleChange={handleChangeDate}
          maxValue={new Date(currentDate)}
        />
      </div>
      <div style={styles.iconsWrap}>
        {onSave && (
          <RoundedIconContainer
            isDisabled={saveDisabled}
            handleClick={() => {
              if (!saveDisabled) onSave();
            }}
          >
            <CheckSingleIcon
              size={16}
              fill={
                saveDisabled
                  ? theme.colors_ds.NEUTRAL_ICON_WEAK
                  : theme.colors_ds.PRIMARY_ICON_MAIN
              }
            />
          </RoundedIconContainer>
        )}
        <RoundedIconContainer
          handleClick={() => {
            setEditMode(false);
            setDate("");
            setValue("");
          }}
        >
          <BinSvg />
        </RoundedIconContainer>
      </div>
    </div>
  );
};


export default AddNewBiomarkerWidget;

const styles = {
  iconsWrap: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  modalCard: (theme: any) => ({
    padding: "12px 16px",
    border: `1px solid ${theme.colors_ds.PRIMARY_TEXT_MAIN}`,
    borderRadius: "8px",
    display: "flex",
    // gap: "16px",
    alignItems: "center",
    justifyContent: "space-between",
  }),
};
